import React, { useState, useEffect, useCallback, useRef } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import cn from "classnames";

import { useHttp } from "hooks/http.hook";
import Label from "components/InputsField/Label";
import InputError from "components/InputsField/InputError";

import styles from "./styles.module.scss";

// Меню живёт в body, поэтому его нужно поднять над оверлеем модалки (.overlay, z-index 2000)
const SELECT_STYLES = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const CheckboxOption = (props) => {
    return (
        <components.Option {...props}>
            <div className={cn(styles.checkboxOption, {
                [styles["checkboxOption--checked"]]: props.isSelected
            })}>
                <span className={styles.checkboxOption__label}>{props.label}</span>
            </div>
        </components.Option>
    );
};

const LimitedMultiValue = ({ maxDisplayed = 2 }) => {
    return (props) => {
        const { index, getValue } = props;
        const selectedValues = getValue();

        if (index < maxDisplayed) {
            return <components.MultiValue {...props} />;
        }

        if (index === maxDisplayed) {
            const remaining = selectedValues.length - maxDisplayed;
            return (
                <div className={styles.moreValues}>
                    +{remaining}...
                </div>
            );
        }

        return null;
    };
};

const FormikAsyncSelect = (props) => {
    const { url, label, name, required, className, selectClassName, errorMode, sort=false, onClear, isMulti, maxDisplayedValues = 3 } = props;

    const { t } = useTranslation();
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const [field, meta] = useField({ name });
    const [defaultValue, setDefaultValue] = useState(null);
    const [options, setOptions] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const { request } = useHttp();
    const prevUrl = useRef();
    const prevValue = useRef();

    const loadOptions = useCallback(async () => {
        setLoading(true);
        prevUrl.current = url;
        return request(url)
            .then((result) => {
                const formatedOptions = result.data.map((item) => ({
                    value: item.id,
                    label: item.value,
                }));

                let value = null;
                if (isMulti) {
                    value = formatedOptions.filter((option) => field.value?.includes(option.value));
                } else {
                    value = formatedOptions.find((option) => option.value === field.value);
                }

                const sortedOptions = sort
                    ? formatedOptions.sort((a, b) => a.label.localeCompare(b.label))
                    : formatedOptions;

                setOptions(sortedOptions);
                setDefaultValue(value);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [field.value, isMulti, request, sort, url]);

    const handleFocus = () => {
        if ((!options.length || prevUrl.current !== url) && url) {
            loadOptions();
        }
    };

    useEffect(() => {
        if (props.options) {
            setOptions(props.options);
            if (field.value) {
                if (isMulti) {
                    setDefaultValue(
                        props.options.filter((option) => field.value?.includes(option.value)),
                    );
                } else {
                    setDefaultValue(props.options.find((option) => option.value === field.value));
                }
            } else {
                setDefaultValue(null);
            }
        }
    }, [props.options, isMulti, field.value]);

    useEffect(() => {
        // Skip if options are provided via props - first useEffect handles that
        if (props.options) {
            return;
        }

        if (!field.value) {
            setDefaultValue(null);
            return;
        }

        // If value exists but options not loaded yet, load them
        if (options.length === 0 && url && !isLoading) {
            loadOptions();
            return;
        }

        // If options are already loaded and value changed, re-match the value
        if (options.length > 0 && field.value !== prevValue.current) {
            prevValue.current = field.value;
            let value = null;
            if (isMulti) {
                value = options.filter((option) => field.value?.includes(option.value));
            } else {
                value = options.find((option) => option.value === field.value);
            }
            setDefaultValue(value);
        }

        // Only fetch when URL changes, not when value changes
        if (url && prevUrl.current !== url) {
            prevValue.current = field.value;
            loadOptions();
        }
    }, [field.value, loadOptions, props.options, url, options, isMulti, isLoading]);

    const handleChange = (option) => {
        if (isMulti) {
            setFieldValue(name, option ? option.map((item) => item.value) : null);
        } else {
            setFieldValue(name, option ? option.value : null);
        }

        setDefaultValue(option);

        if (!option && onClear) {
            onClear();
        }
    };

    return (
        <div className={cn(styles.wrapper, className)}>
            <Label name={name} label={label} required={required} />
            <Select
                onFocus={handleFocus}
                onBlur={() => setFieldTouched(name)}
                isSearchable={true}
                isLoading={isLoading}
                options={options}
                classNamePrefix="select"
                inputId={name}
                onChange={handleChange}
                value={defaultValue}
                loadingMessage={() => t("loading")}
                noOptionsMessage={() => t("errors.noOptions")}
                placeholder={t("defaultPlaceholder")}
                closeMenuOnSelect={!isMulti}
                hideSelectedOptions={false}
                // Внутри модалки и других блоков с overflow меню обрезалось по границе
                // контейнера — портал в body снимает ограничение, fixed держит его у поля
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={SELECT_STYLES}
                components={isMulti ? {
                    Option: CheckboxOption,
                    ...(maxDisplayedValues ? { MultiValue: LimitedMultiValue({ maxDisplayed: maxDisplayedValues }) } : {})
                } : undefined}
                {...props}
                className={cn(styles.select, selectClassName, {
                    [styles["select--error"]]: meta.touched && meta.error,
                })}
            />
            {meta.touched && meta.error && (
                <InputError message={meta.error} mode={errorMode || "top-right"} />
            )}
        </div>
    );
};

export default FormikAsyncSelect;
